import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Button, Tooltip, Box, Chip, Text, Grid, Notification, Flex, ColorDot, Label } from "@telia/teddy";
import cn from "classnames";
import _get from "lodash/get";
import { useSelector, useDispatch } from "react-redux";
import { FORM_ID } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { HardwareLookedUpProduct } from "../../../../../../models/state";
import { getFormattedPrice } from "../../../../../../shared/priceFormatter";
import { getActiveSubscription, getAllHardwareBundleItems } from "../../../../checkout/state/selectors/cartSelectors";
import { FieldSet, Input } from "../../../../forms";
import { isTypeLoading } from "../../../../loading/state/selectors";
import { RapidForm, actionTypes as RF_AT } from "../../../../rapidform";
import {
    allValidationRules,
    MAX_PRICE_FOR_LEASE_RESELLER,
    VALIDATION_FIELD,
} from "../../../../rapidform/formValidation";
import { updateProductAndEan } from "../../../state/actions/hardwareActions";
import { RETAILER_ADD_EXTERNAL_HARDWARE, RETAILER_FETCH_PRODUCT_BY_EAN } from "../../../state/actionTypes";
import { getFormById } from "../../../state/selectors/formSelectors";
import { getCustomerCreditCheck } from "../../../state/selectors/customerSelectors";
import "./EquipmentForm.less";
import { getLookedUpProducts, getSelectedEan } from "../../../state/selectors/hardwareSelectors";

const COMMITMENT_DURATION_OPTIONS = [
    { label: "Ingen", value: 0 },
    { label: "12", value: 12 },
    { label: "24", value: 24 },
    { label: "36", value: 36 },
    { label: "48", value: 48 },
];

const MINIMUM_MONTHLY_PRICE = 24;

export const normalizeCommitmentDurationSelection = (
    selectedCommitmentDuration: string | undefined,
    availableDurationOptions: Array<{ label: string; value: number }>,
): string | undefined =>
    selectedCommitmentDuration &&
    !availableDurationOptions.some(({ value }) => value.toString() === selectedCommitmentDuration)
        ? "0"
        : selectedCommitmentDuration;

export const calculateInstallmentPlan = (
    hardwarePrice: string,
    selectedCommitmentDuration: string | undefined,
): { monthlyPrice: number | null; lastMonthPrice: number | null } => {
    if (!hardwarePrice || !selectedCommitmentDuration || selectedCommitmentDuration === "0") {
        return { monthlyPrice: null, lastMonthPrice: null };
    }

    const price = parseFloat(hardwarePrice);
    const duration = parseInt(selectedCommitmentDuration, 10);

    if (duration === 0) {
        return { monthlyPrice: 0, lastMonthPrice: 0 };
    }

    const monthly = Math.ceil(parseFloat((price / duration).toFixed(2)));
    // Rounding the monthly amount up (instead of down) means the remainder from the division is
    // subtracted from the last installment, so it is normally smaller than the other installments.
    // With a very low price relative to the duration this remainder can reach zero or go negative,
    // which is invalid and must be caught by the caller (the last installment must always be > 0).
    const lastMonth = price - monthly * (duration - 1);

    return { monthlyPrice: monthly, lastMonthPrice: lastMonth };
};

interface Props {
    onEanLookup: (ean: string) => void;
    onSubmit: (selectedCommitmentDuration?: string, imei?: string) => void;
}

const isFieldValid = (field: string, value: string): boolean => {
    const rule = allValidationRules[field];
    if (!rule || !value) return false;
    if (rule.regex && !rule.regex.test(value)) return false;
    if (rule.validateFn && rule.validateFn(value)) return false;
    return true;
};

const EquipmentForm: React.FC<Props> = ({ onSubmit, onEanLookup }) => {
    const dispatch = useDispatch();
    const isLookingUpEan = useSelector((state: RootState) => isTypeLoading(state, RETAILER_FETCH_PRODUCT_BY_EAN));
    const isLoading = useSelector((state: RootState) => isTypeLoading(state, RETAILER_ADD_EXTERNAL_HARDWARE));
    const form = useSelector((state: RootState) => getFormById(state, FORM_ID.EQUIPMENT_REGISTRATION));
    const lookedUpProducts = useSelector((state: RootState) => getLookedUpProducts(state));
    const selectedEan = useSelector((state: RootState) => getSelectedEan(state));
    const lookedUpProduct = lookedUpProducts?.find((product) => product.ean === selectedEan);
    const eanInputRef = React.useRef<HTMLInputElement>(null);
    const imeiInputRef = React.useRef<HTMLInputElement>(null);
    const modelInputRef = React.useRef<HTMLInputElement>(null);
    const totalPriceInputRef = React.useRef<HTMLInputElement>(null);
    const wasLookingUpEan = React.useRef(false);
    const activeSubscription = useSelector((state: RootState) => getActiveSubscription(state));
    const hardwareBundles = useSelector((state: RootState) => getAllHardwareBundleItems(state));
    const creditCheck = useSelector((state: RootState) => getCustomerCreditCheck(state));
    const hasSubscriptionSpecificLoanLimits =
        !!creditCheck?.maxLoanAmountBySubscriptionId ||
        !!creditCheck?.durationOptionsBySubscriptionId ||
        !!creditCheck?.minMonthlyPriceBySubscriptionId;
    const subscriptionMaxLoanAmount = activeSubscription
        ? creditCheck?.maxLoanAmountBySubscriptionId?.[activeSubscription]
        : undefined;
    const subscriptionDurationOptions = activeSubscription
        ? creditCheck?.durationOptionsBySubscriptionId?.[activeSubscription]
        : undefined;
    const subscriptionMinMonthlyPrice = activeSubscription
        ? creditCheck?.minMonthlyPriceBySubscriptionId?.[activeSubscription]
        : undefined;
    const effectiveLeaseRestrictionAmount =
        subscriptionMaxLoanAmount ??
        (!hasSubscriptionSpecificLoanLimits ? creditCheck?.maxLoanAmount : undefined) ??
        creditCheck?.leaseRestrictionAmount;
    const allowedDurationValues =
        subscriptionDurationOptions ??
        (!creditCheck?.durationOptionsBySubscriptionId ? creditCheck?.durationOptions : undefined);
    // Only offer the lease durations NEO actually allows for this customer (in addition to "Ingen"/upfront,
    // which is always available regardless of loan limits). Falls back to showing all options if NEO's
    // duration_options isn't available (e.g. lookup failed), so the form still works without hard-blocking.
    const availableDurationOptions = allowedDurationValues
        ? COMMITMENT_DURATION_OPTIONS.filter(({ value }) => value === 0 || allowedDurationValues.includes(value))
        : COMMITMENT_DURATION_OPTIONS;
    const minimumMonthlyPrice =
        subscriptionMinMonthlyPrice ??
        (!creditCheck?.minMonthlyPriceBySubscriptionId ? creditCheck?.minMonthlyPrice : undefined) ??
        MINIMUM_MONTHLY_PRICE;

    const eanValue = _get(form, `values.equipment.${VALIDATION_FIELD.EAN}`, "");
    const imeiValue = _get(form, `values.equipment.${VALIDATION_FIELD.IMEI}`, "");
    const hardwarePrice = _get(form, `values.equipment.${VALIDATION_FIELD.HARDWARE_PRICE}`, "");
    const hardwareCommitmentDuration = _get(
        form,
        `values.equipment.${VALIDATION_FIELD.HARDWARE_COMMITMENT_DURATION}`,
        "",
    );
    const hardwareModel = _get(form, `values.equipment.${VALIDATION_FIELD.HARDWARE_MODEL}`, "");

    const [imeiUnavailable, setImeiUnavailable] = useState(() => !!hardwareCommitmentDuration && !imeiValue);
    const [selectedCommitmentDuration, setSelectedCommitmentDuration] = useState<string | undefined>(
        () => hardwareCommitmentDuration || "0",
    );
    const [eanUnavailable, setEanUnavailable] = useState(hardwareModel && !eanValue);

    const isLeaseBlockedForDOrTNumber = !!creditCheck?.isDOrTNumber && selectedCommitmentDuration !== "0";

    const isEanValid = isFieldValid(VALIDATION_FIELD.EAN, eanValue);
    const isImeiAlreadyInUse =
        !imeiUnavailable &&
        !!imeiValue &&
        hardwareBundles.some((bundle) => bundle.bundleId !== activeSubscription && bundle.imei === imeiValue);
    const formErrors = isImeiAlreadyInUse
        ? {
              ...form?.errors,
              equipment: {
                  ...form?.errors?.equipment,
                  [VALIDATION_FIELD.IMEI]:
                      form?.errors?.equipment?.[VALIDATION_FIELD.IMEI] ||
                      "IMEI er allerede i bruk av et annet produkt i handlekurven",
              },
          }
        : form?.errors || {};

    const handleSelectColor = (product: HardwareLookedUpProduct) => {
        dispatch(updateProductAndEan(product.ean, product.model));
    };

    const handleSubmit = () => {
        const imei = imeiUnavailable ? undefined : imeiValue;
        onSubmit(selectedCommitmentDuration, imei);
    };

    const { monthlyPrice, lastMonthPrice } = useMemo(
        () => calculateInstallmentPlan(hardwarePrice, selectedCommitmentDuration),
        [selectedCommitmentDuration, hardwarePrice],
    );

    useEffect(() => {
        if (hardwareCommitmentDuration) {
            setSelectedCommitmentDuration(hardwareCommitmentDuration);
        }
    }, [hardwareCommitmentDuration]);

    useEffect(() => {
        const normalizedCommitmentDuration = normalizeCommitmentDurationSelection(
            selectedCommitmentDuration,
            availableDurationOptions,
        );

        if (normalizedCommitmentDuration !== selectedCommitmentDuration) {
            setSelectedCommitmentDuration(normalizedCommitmentDuration);
        }
    }, [availableDurationOptions, selectedCommitmentDuration]);

    useEffect(() => {
        if (selectedCommitmentDuration === "0") {
            dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.EQUIPMENT_REGISTRATION),
                payload: {
                    errorMessage: undefined,
                    form: FORM_ID.EQUIPMENT_REGISTRATION,
                    inputName: `equipment-${VALIDATION_FIELD.HARDWARE_PRICE}`,
                    inputValue: "",
                    visible: false,
                },
            });
        } else {
            dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.EQUIPMENT_REGISTRATION),
                payload: {
                    errorMessage: undefined,
                    form: FORM_ID.EQUIPMENT_REGISTRATION,
                    inputName: `equipment-${VALIDATION_FIELD.HARDWARE_PRICE}`,
                    inputValue: hardwarePrice,
                    started: false,
                    visible: true,
                },
            });
        }
    }, [dispatch, selectedCommitmentDuration, hardwarePrice]);

    useEffect(() => {
        if (wasLookingUpEan.current && !isLookingUpEan && !imeiUnavailable) {
            setTimeout(() => {
                imeiInputRef?.current?.focus();
            }, 0);
        }
        wasLookingUpEan.current = isLookingUpEan;
    }, [isLookingUpEan, imeiUnavailable]);

    useEffect(() => {
        if (eanInputRef.current) {
            setTimeout(() => {
                eanInputRef?.current?.focus();
            }, 0);
        }
    }, [activeSubscription]);

    useEffect(() => {
        if (eanUnavailable) {
            setTimeout(() => {
                modelInputRef?.current?.focus();
            }, 0);
        }
    }, [eanUnavailable]);

    useEffect(() => {
        if (imeiUnavailable) {
            setTimeout(() => {
                totalPriceInputRef?.current?.focus();
            }, 0);
        }
    }, [imeiUnavailable]);

    useEffect(() => {
        if (/^\d{15}$/.test(imeiValue)) {
            setTimeout(() => {
                totalPriceInputRef?.current?.focus();
            }, 0);
        }
    }, [imeiValue]);

    useEffect(() => {
        if (eanValue) {
            onEanLookup(eanValue);
        }
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, [activeSubscription]);

    return (
        <RapidForm
            id={FORM_ID.EQUIPMENT_REGISTRATION}
            action="#"
            values={form?.values || {}}
            visible={form?.visible || {}}
            started={form?.started || {}}
            errors={formErrors}
            onSubmit={handleSubmit}
        >
            <FieldSet name="equipment">
                <div className="equipment-form">
                    <div className="equipment-form__field">
                        <div className="equipment-form__field-input">
                            <LabelWithTooltip
                                htmlFor="ean-input"
                                label="EAN: Søk for å finne pris og modell"
                                tooltipContent={
                                    <>
                                        EAN finnes på produktets emballasje eller strekkode. Det er vanligvis 8, 12, 13
                                        eller 14 siffer.
                                    </>
                                }
                            />
                            <Input
                                id="ean-input"
                                type="text"
                                name={VALIDATION_FIELD.EAN}
                                inputRef={eanInputRef}
                                disabled={eanUnavailable}
                                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                                    if (event.key === "Enter" && isEanValid && !isLookingUpEan) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onEanLookup(eanValue);
                                    }
                                }}
                                onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
                                    const pastedText = event.clipboardData
                                        .getData("text")
                                        .trim()
                                        .replace(/\D/g, "")
                                        .substring(0, 14);
                                    if (/^\d{8,}$/.test(pastedText) && !isLookingUpEan) {
                                        setTimeout(() => onEanLookup(pastedText), 0);
                                    }
                                }}
                            />
                        </div>
                        <Button
                            disabled={!isEanValid || isLookingUpEan || eanUnavailable}
                            loading={isLookingUpEan}
                            mt="100"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onEanLookup(eanValue);
                            }}
                            className="equipment-form__ean-button"
                        >
                            Søk
                        </Button>
                    </div>
                    <Checkbox className="equipment-form__checkbox">
                        <Checkbox.Trigger
                            checked={eanUnavailable}
                            onCheckedChange={(state) => setEanUnavailable(!!state)}
                        />
                        <span>Legg til modell og pris manuelt</span>
                    </Checkbox>
                    <Box mt="100">
                        {(!!lookedUpProducts || eanUnavailable || hardwareCommitmentDuration) && (
                            <>
                                <Flex mb="100" align="start" className="equipment-form__model-wrapper">
                                    {lookedUpProduct?.image?.url && !eanUnavailable && (
                                        <div className="equipment-form__image-container">
                                            <img
                                                className="equipment-form__image"
                                                src={lookedUpProduct.image?.url}
                                                alt=""
                                            />
                                        </div>
                                    )}
                                    <Flex direction="column" width="100%">
                                        {lookedUpProducts && lookedUpProducts.length > 1 && !eanUnavailable && (
                                            <>
                                                <Label className="equipment-form__label equipment-form__label-color">
                                                    Velg farge
                                                </Label>
                                                <Flex
                                                    className="equipment-form__color-options"
                                                    wrap="wrap"
                                                    gap="150"
                                                    mt="50"
                                                    mb="250"
                                                >
                                                    {lookedUpProducts?.map((variant) => (
                                                        <button
                                                            key={variant.ean}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleSelectColor(variant);
                                                            }}
                                                            className={cn("equipment-form__color-button", {
                                                                selected: variant.ean === selectedEan,
                                                            })}
                                                            aria-pressed={variant.ean === selectedEan}
                                                            aria-label={`Velg ${variant.colorName || "ukjent farge"}`}
                                                        >
                                                            <ColorDot
                                                                size="md"
                                                                bordered
                                                                className={cn({
                                                                    ["equipment-form__color-button--selected"]:
                                                                        variant.ean === selectedEan,
                                                                })}
                                                                color={
                                                                    variant.htmlColor ??
                                                                    variant.colorName ??
                                                                    "transparent"
                                                                }
                                                            />
                                                        </button>
                                                    ))}
                                                </Flex>
                                            </>
                                        )}

                                        <div className="equipment-form__field equipment-form__field--model">
                                            <div className="equipment-form__field-input">
                                                <LabelWithTooltip
                                                    htmlFor="modell-input"
                                                    label="Modell"
                                                    tooltipContent={
                                                        <>
                                                            Rett modell er et krav. Dette blir presentert på kundens
                                                            faktura
                                                        </>
                                                    }
                                                />
                                                <Input
                                                    id="modell-input"
                                                    type="text"
                                                    name={VALIDATION_FIELD.HARDWARE_MODEL}
                                                    inputRef={modelInputRef}
                                                />
                                            </div>
                                        </div>
                                    </Flex>
                                </Flex>
                                <Flex direction="column" width="100%">
                                    {!imeiUnavailable && (
                                        <div className="equipment-form__field">
                                            <div className="equipment-form__field-input">
                                                <LabelWithTooltip
                                                    htmlFor="imei-input"
                                                    label="IMEI"
                                                    isTabbable={false}
                                                    tooltipContent={
                                                        <>
                                                            IMEI-nummer består av 15 sifre. Du finner det vanligvis på
                                                            baksiden av enhetens emballasje.
                                                        </>
                                                    }
                                                />
                                                <Input
                                                    id="imei-input"
                                                    type="text"
                                                    name={VALIDATION_FIELD.IMEI}
                                                    inputRef={imeiInputRef}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <Checkbox className="equipment-form__checkbox">
                                        <Checkbox.Trigger
                                            checked={imeiUnavailable}
                                            onCheckedChange={(state) => setImeiUnavailable(!!state)}
                                        />
                                        <span>Har ikke IMEI tilgjengelig og ønsker å etterregistrere det senere</span>
                                    </Checkbox>
                                </Flex>
                                <Box mt="200" mb="200">
                                    <Label className="equipment-form__label">Delbetalingstid i måneder</Label>
                                    <Chip
                                        className="equipment-form__chip"
                                        type="single"
                                        value={selectedCommitmentDuration ?? "12"}
                                        onValueChange={(val) => {
                                            if (!val) return;

                                            setSelectedCommitmentDuration(val);

                                            if (val !== "0" && String(hardwarePrice || "").trim() === "") {
                                                setTimeout(() => {
                                                    totalPriceInputRef?.current?.focus();
                                                }, 0);
                                            }
                                        }}
                                        variant="choice"
                                    >
                                        {availableDurationOptions.map(({ value, label }) => (
                                            <Chip.Item
                                                className="equipment-form__chip-item"
                                                key={value}
                                                value={value.toString()}
                                            >
                                                {label}
                                            </Chip.Item>
                                        ))}
                                    </Chip>
                                    {isLeaseBlockedForDOrTNumber && (
                                        <Notification type="tile" variant="warning" mt="200">
                                            <Notification.Icon />
                                            <Notification.Text>
                                                Kunder med D-nummer kan ikke få delbetaling. Velg «Ingen» for å
                                                fortsette.
                                            </Notification.Text>
                                        </Notification>
                                    )}
                                </Box>
                                {selectedCommitmentDuration !== "0" && (
                                    <div className="equipment-form__field">
                                        <div className="equipment-form__field-input">
                                            <LabelWithTooltip
                                                htmlFor="total-input"
                                                label="Total delbetalingsbeløp"
                                                tooltipContent={
                                                    <>
                                                        Dette er det beløpet kunden skal nedbetale til Telia. (Dette kan
                                                        da være deres telefonpris pluss evt. tilbehør, trukket fra
                                                        rabatt og hva kunden betaler i butikk).
                                                    </>
                                                }
                                            />
                                            <Input
                                                id="total-input"
                                                type="text"
                                                name={VALIDATION_FIELD.HARDWARE_PRICE}
                                                disabled={selectedCommitmentDuration === "0"}
                                                inputRef={totalPriceInputRef}
                                            />
                                        </div>
                                        {effectiveLeaseRestrictionAmount !== undefined &&
                                            selectedCommitmentDuration !== "0" &&
                                            hardwarePrice > effectiveLeaseRestrictionAmount && (
                                                <Notification type="tile" variant="error" style={{ flexBasis: "100%" }}>
                                                    <Notification.Icon />
                                                    <Notification.Text>
                                                        Total delbetaling kan ikke overstige{" "}
                                                        {effectiveLeaseRestrictionAmount.toLocaleString("nb-NO")} kr for
                                                        valgt nummer. En eventuell differanse mellom mobilens pris og
                                                        delbetalingsbeløpet må betales i sin helhet i butikk.
                                                    </Notification.Text>
                                                </Notification>
                                            )}
                                        {effectiveLeaseRestrictionAmount !== undefined &&
                                            selectedCommitmentDuration !== "0" &&
                                            !(hardwarePrice > effectiveLeaseRestrictionAmount) && (
                                                <Notification
                                                    type="tile"
                                                    variant="information"
                                                    style={{ flexBasis: "100%" }}
                                                >
                                                    <Notification.Icon />
                                                    <Notification.Text>
                                                        Kunde har en delbetalingsgrense på{" "}
                                                        {effectiveLeaseRestrictionAmount.toLocaleString("nb-NO")} kr.
                                                    </Notification.Text>
                                                </Notification>
                                            )}
                                    </div>
                                )}
                                {selectedCommitmentDuration &&
                                    monthlyPrice !== null &&
                                    lastMonthPrice !== null &&
                                    !(
                                        effectiveLeaseRestrictionAmount !== undefined &&
                                        selectedCommitmentDuration !== "0" &&
                                        hardwarePrice > effectiveLeaseRestrictionAmount
                                    ) && (
                                        <Grid
                                            className="equipment-form__grid"
                                            mt="200"
                                            columns={"2"}
                                            gap={{ sm: "10", md: "100", lg: "100", xl: "25" }}
                                        >
                                            <Text variant="paragraph-100-bold" style={{ gridColumn: "span 2" }}>
                                                <strong>Delbetalingsplan</strong>
                                            </Text>
                                            <Text variant="paragraph-100">Månedlig delbetaling:</Text>
                                            <Text as="span" variant="paragraph-100-bold">
                                                {getFormattedPrice(monthlyPrice)}
                                            </Text>
                                            {monthlyPrice < minimumMonthlyPrice && (
                                                <Notification
                                                    type="tile"
                                                    variant="error"
                                                    style={{ gridColumn: "span 2" }}
                                                >
                                                    <Notification.Icon />
                                                    <Notification.Text>
                                                        Størrelsen på avdragene er for lavt. <br />
                                                        Minste størrelse er: {getFormattedPrice(minimumMonthlyPrice)}
                                                        /md.
                                                    </Notification.Text>
                                                </Notification>
                                            )}
                                            <Flex align="center" gap="50">
                                                <Text variant="paragraph-100">Siste delbetaling:</Text>
                                                <Tooltip>
                                                    <Tooltip.Trigger className="equipment-form__tooltip-trigger" />
                                                    <Tooltip.Content className="equipment-form__tooltip-content">
                                                        <Box p="100" maxWidth="300px" style={{ textAlign: "center" }}>
                                                            Siste måned har ofte en noe mindre sum grunnet avrunding
                                                            oppover på de andre månedene.
                                                        </Box>
                                                    </Tooltip.Content>
                                                </Tooltip>
                                            </Flex>
                                            <Text as="span" variant="paragraph-100-bold">
                                                {getFormattedPrice(lastMonthPrice)}
                                            </Text>
                                            {lastMonthPrice <= 0 && (
                                                <Notification
                                                    type="tile"
                                                    variant="error"
                                                    style={{ gridColumn: "span 2" }}
                                                >
                                                    <Notification.Icon />
                                                    <Notification.Text>
                                                        Siste delbetaling kan ikke være 0 kr eller lavere. Velg en
                                                        kortere delbetalingstid eller juster totalbeløpet.
                                                    </Notification.Text>
                                                </Notification>
                                            )}
                                            <Text variant="paragraph-100">Totalt:</Text>
                                            <Text as="span" variant="paragraph-100-bold">
                                                {getFormattedPrice(
                                                    (parseInt(selectedCommitmentDuration) - 1) * monthlyPrice +
                                                        lastMonthPrice,
                                                )}
                                            </Text>
                                        </Grid>
                                    )}
                            </>
                        )}
                    </Box>
                    <Button
                        type="submit"
                        disabled={
                            isLookingUpEan ||
                            isLoading ||
                            isImeiAlreadyInUse ||
                            isLeaseBlockedForDOrTNumber ||
                            hardwarePrice > MAX_PRICE_FOR_LEASE_RESELLER ||
                            (effectiveLeaseRestrictionAmount !== undefined &&
                                selectedCommitmentDuration !== "0" &&
                                hardwarePrice > effectiveLeaseRestrictionAmount) ||
                            (selectedCommitmentDuration !== undefined &&
                                monthlyPrice !== null &&
                                monthlyPrice < minimumMonthlyPrice) ||
                            (selectedCommitmentDuration !== undefined &&
                                lastMonthPrice !== null &&
                                lastMonthPrice <= 0) ||
                            ((!!lookedUpProducts || eanUnavailable || !!hardwareCommitmentDuration) &&
                                !imeiUnavailable &&
                                !isFieldValid(VALIDATION_FIELD.IMEI, imeiValue)) ||
                            (!eanUnavailable && !lookedUpProducts)
                        }
                        loading={isLoading}
                        mt="300"
                    >
                        Fortsett
                    </Button>
                </div>
            </FieldSet>
        </RapidForm>
    );
};

export default EquipmentForm;
export { EquipmentForm };

const LabelWithTooltip = ({
    label,
    tooltipContent,
    htmlFor,
    isTabbable = true,
}: {
    htmlFor: string;
    label: string;
    tooltipContent: React.ReactNode;
    isTabbable?: boolean;
}) => (
    <div className="equipment-form__label-with-tooltip">
        <Label className="equipment-form__label" htmlFor={htmlFor}>
            {label}
        </Label>
        <div className="equipment-form__tooltip-container">
            <Tooltip>
                <Tooltip.Trigger
                    tabIndex={isTabbable ? 0 : -1}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                    className="equipment-form__tooltip-trigger"
                />
                <Tooltip.Content className="equipment-form__tooltip-content">
                    <Box p="100" maxWidth="300px" style={{ textAlign: "center" }}>
                        {tooltipContent}
                    </Box>
                </Tooltip.Content>
            </Tooltip>
        </div>
    </div>
);
